import React from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import PropertySwiper from "./PropertySwiper";
import SearchIcon from "../images/search.svg";
import MeterIcon from "../images/meter.png";
import BedroomsIcon from "../images/bedrooms.png";
import AddressIcon from "../images/address.png";
import PriceIcon from "../images/price.png";
import UnitsIcon from "../images/units.png";
import styles from "./NewSearchesSwiper.module.css";
import { useSponsors } from "../hooks/useSponsors";

const capitalizeFirstLetter = (string) => {
  if (!string) return '';
  return string.charAt(0).toUpperCase() + string.slice(1);
};

const formatWithApostrophe = (value) => {
  if (!value && value !== 0) return '';
  const numValue = typeof value === 'string' ? parseFloat(value.replace(/'/g, '')) : value;
  if (isNaN(numValue)) return value;
  const digitsOnly = numValue.toString().replace(/[^\d]/g, '');
  return digitsOnly.replace(/\B(?=(\d{3})+(?!\d))/g, "'");
};

/**
 * NewSearchesSwiper - Displays search/demand properties using OfferProperty layout
 * Shows card header with property type, address, area, bedrooms, price
 * Used across all pages for consistent search property display
 */
const NewSearchesSwiper = ({ items, className }) => {
  const { t } = useTranslation();
  // NewSearchesSwiper shows on the offer screen → fetch offer-targeted sponsors
  const { getSponsor } = useSponsors('offer');

  // Add sponsors after every 3rd property
  const itemsWithSponsor = React.useMemo(() => {
    if (!items || items.length === 0) return [];

    const result = [];
    let sponsorCounter = 0;

    items.forEach((item, index) => {
      result.push(item);

      // Add sponsor after every 3rd property
      if ((index + 1) % 3 === 0) {
        result.push({
          id: `sponsor-${sponsorCounter}`,
          isSponsor: true,
          sponsorIndex: sponsorCounter,
        });
        sponsorCounter++;
      }
    });

    return result;
  }, [items]);

  // Create a unique key based on items to force re-render when data changes
  const swiperKey = React.useMemo(() => {
    if (!items || items.length === 0) return 'empty';
    return items.map(item => item.id || item.displayId).join('-');
  }, [items]);

  if (!items || items.length === 0) {
    return null;
  }

  return (
    <PropertySwiper
      key={swiperKey}
      items={itemsWithSponsor}
      className={className}
      userType="offer"
      renderItem={(item) =>
        item.isSponsor ? (
          <SponsorCard sponsor={getSponsor(item.sponsorIndex)} styles={styles} />
        ) : (
          <div className={styles.propertyCard}>
            <div>
              <div
                className={
                  item.offeringType === "rental"
                    ? styles.redCardHeader
                    : styles.cardHeader
                }
              >
                <div className={styles.searchIconWrapper}>
                  <img src={SearchIcon} alt="search icon" className={styles.searchIcon} />
                </div>
                <p className={styles.cardTitle}>
                  {`${t(`propertyType.${item.propertyType?.toLowerCase() || 'house/villa'}`).toUpperCase()}`}
                  {item.offeringType === "purchase" && ` ${t('common.to_buy').toUpperCase()}`}
                  {item.offeringType === "rental" && ` ${t('common.for_rent').toUpperCase()}`}
                </p>
              </div>
            </div>

            <div className={styles.propertyDetails}>
              <div className={styles.propertyLocation}>
                <img src={AddressIcon} alt="address icon" className={styles.statIcon} />
                <p className={styles.locationText}>
                  {(() => {
                    const zipCode = item.zipCode || '';
                    const city = item.city || '';
                    const country = item.country || item.countryName || '';
                    const parts = [zipCode, city, country].filter(part => part && part.trim() !== '');
                    return parts.length > 0 ? parts.join(', ') : 'N/A';
                  })()}
                </p>
              </div>

              <div className={styles.propertyStats}>
                <div className={styles.statLine}>
                  <img src={MeterIcon} alt="area icon" className={styles.statIcon} />
                  <span className={styles.statText}>{item.squareMeters || "N/A"} m²</span>
                </div>

                <div className={styles.statLine}>
                  {item.bedrooms ? (
                    <img src={BedroomsIcon} alt="bedrooms icon" className={styles.statIcon} />
                  ) : item.units ? (
                    <img src={UnitsIcon} alt="units icon" className={styles.statIcon} />
                  ) : item.index != null ? (
                    <img src={MeterIcon} alt="index icon" className={styles.statIcon} />
                  ) : null}
                  <span className={styles.statText}>
                    {item.bedrooms
                      ? `${item.bedrooms} ${capitalizeFirstLetter(parseInt(item.bedrooms) === 1 ? t('bed.one') : t('bed.other'))}`
                      : item.units
                      ? `${item.units} ${capitalizeFirstLetter(parseInt(item.units) === 1 ? t('units.one') : t('units.other'))}`
                      : item.index != null
                      ? `${t('Index')}: ${item.index}`
                      : "N/A"}
                  </span>
                </div>

                <div className={styles.statLine}>
                  <img src={PriceIcon} alt="price icon" className={styles.statIcon} />
                  <span className={styles.statText}>
                    CHF {item.price ? formatWithApostrophe(item.price) : "N/A"}
                  </span>
                </div>
              </div>
            </div>
          </div>
        )
      }
    />
  );
};

function SponsorCard({ sponsor, styles }) {
  const { t } = useTranslation();

  if (!sponsor) {
    return (
      <div className={styles.propertyCardSponcer}>
        <Link to="/support" className={styles.sponsorPlaceholder}>
          <p className={styles.sponsorPlaceholderTitle}>{t('sponsor.space_available')}</p>
          <p className={styles.sponsorPlaceholderSub}>{t('sponsor.contact_details')}</p>
        </Link>
      </div>
    );
  }

  const card = (
    <div className={styles.propertyCardSponcer}>
      <div className={styles.propertyImageSponcer}>
        <img
          src={sponsor.imageUrl}
          alt={sponsor.title}
          className={styles.propertyImg2}
        />
      </div>
    </div>
  );

  if (sponsor.sponsorUrl) {
    return (
      <a href={sponsor.sponsorUrl} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none' }}>
        {card}
      </a>
    );
  }

  return card;
}

export default NewSearchesSwiper;
